import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';

export interface Promotion {
  id:            string;
  productId:     string;
  name:          string;
  type:          'bogo' | 'nxm' | 'percentage' | 'fixed' | 'bundle';
  buyQty:        number | null;
  payQty:        number | null;
  discountValue: number | null;
  label:         string;
  description:   string | null;
  startsAt:      number | null;
  endsAt:        number | null;
  isActive:      boolean;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  characteristics: string;
  sizes: string;
  price: number;
  originalPrice?: number | null;
  stock?: number;
  image: string;
  imageSecondary?: string | null;
  images?: string[];
  badge?: string | null;
  isFeatured?: boolean;
  category: string;
  gender?: string;
  promotion?: Promotion | null;
}

export interface CartItem {
  product: Product;
  quantity: number;
  size: string;
}

interface CartState {
  items: CartItem[];
  isCartOpen: boolean;
}

const cartSlice = createSlice({
  name: 'cart',
  initialState: { items: [], isCartOpen: false } as CartState,
  reducers: {
    addToCart(state, { payload }: PayloadAction<{ product: Product; size?: string }>) {
      const chosenSize = payload.size || (payload.product.sizes?.split(', ')[0] ?? 'Único');
      const existing = state.items.find(
        i => i.product.id === payload.product.id && i.size === chosenSize
      );
      if (existing) {
        existing.quantity += 1;
      } else {
        state.items.push({ product: payload.product, quantity: 1, size: chosenSize });
      }
    },
    removeFromCart(state, { payload }: PayloadAction<{ productId: string; size: string }>) {
      state.items = state.items.filter(
        i => !(i.product.id === payload.productId && i.size === payload.size)
      );
    },
    updateQuantity(state, { payload }: PayloadAction<{ productId: string; size: string; quantity: number }>) {
      if (payload.quantity <= 0) {
        state.items = state.items.filter(
          i => !(i.product.id === payload.productId && i.size === payload.size)
        );
      } else {
        const item = state.items.find(
          i => i.product.id === payload.productId && i.size === payload.size
        );
        if (item) item.quantity = payload.quantity;
      }
    },
    clearCart(state) {
      state.items = [];
    },
    setIsCartOpen(state, { payload }: PayloadAction<boolean>) {
      state.isCartOpen = payload;
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart, setIsCartOpen } = cartSlice.actions;
export default cartSlice.reducer;

// ─── Promotion Calculations ──────────────────────────────────────────────────

export interface PromoResult {
  originalTotal:  number;
  discountedTotal: number;
  savings:         number;
  freeUnits:       number;
  pricePerUnit:    number;
}

export interface PromoPriceDisplay {
  price:         number;         // precio final por unidad (1 unidad)
  originalPrice: number | null;  // precio sin descuento, para tachar
  percentOff:    number | null;
  caption:       string | null;  // leyenda para promos por cantidad
}

const money = (n: number) => `$${Math.round(n).toLocaleString('es-AR')}`;

/**
 * Regla única de display de precio con promoción (catálogo y detalle).
 * - percentage / fixed: precio ya descontado + precio cargado tachado.
 * - bogo / nxm / bundle: precio cargado + leyenda con el precio real por unidad.
 * No toca product.price: el carrito sigue aplicando la promo con calcPromo().
 * Mantener en sync con apps/dashboard/src/utils/promoPrice.ts.
 */
export function describePromoPrice(product: Product): PromoPriceDisplay {
  const promo = product.promotion;
  const base = { price: product.price, originalPrice: product.originalPrice ?? null, percentOff: null, caption: null };

  if (promo?.type === 'percentage' || promo?.type === 'fixed') {
    const final = calcPromo(promo, 1, product.price).pricePerUnit;
    if (final < product.price) {
      return {
        price: final,
        originalPrice: product.price,
        percentOff: Math.round(((product.price - final) / product.price) * 100),
        caption: null,
      };
    }
  }

  if (promo?.type === 'bogo' || promo?.type === 'nxm') {
    const buy = promo.buyQty ?? 2;
    const pay = promo.payQty ?? 1;
    if (buy > pay) {
      return { ...base, caption: `${promo.label} · llevando ${buy} pagás ${money((product.price * pay) / buy)} c/u` };
    }
  }

  if (promo?.type === 'bundle' && promo.discountValue != null && promo.discountValue < product.price) {
    return { ...base, caption: `Llevando ${promo.buyQty ?? 2}: ${money(promo.discountValue)} c/u` };
  }

  return base;
}

export function calcPromo(
  promo: Promotion,
  qty: number,
  unitPrice: number,
): PromoResult {
  const originalTotal = unitPrice * qty;
  let discountedTotal = originalTotal;
  let freeUnits = 0;

  switch (promo.type) {
    case 'bogo':
    case 'nxm': {
      const buyQty = promo.buyQty ?? 2;
      const payQty = promo.payQty ?? 1;
      const groups = Math.floor(qty / buyQty);
      freeUnits = groups * (buyQty - payQty);
      discountedTotal = (qty - freeUnits) * unitPrice;
      break;
    }
    case 'percentage': {
      const pct = Math.min(100, Math.max(0, promo.discountValue ?? 0));
      discountedTotal = Math.round(originalTotal * (1 - pct / 100));
      break;
    }
    case 'fixed': {
      const discountPerUnit = promo.discountValue ?? 0;
      const discountedUnit  = Math.max(0, unitPrice - discountPerUnit);
      discountedTotal = discountedUnit * qty;
      break;
    }
    case 'bundle': {
      const minQty      = promo.buyQty        ?? 2;
      const bundlePrice = promo.discountValue ?? unitPrice;
      if (qty >= minQty) {
        discountedTotal = bundlePrice * qty;
      }
      break;
    }
  }

  return {
    originalTotal,
    discountedTotal,
    savings: Math.max(0, originalTotal - discountedTotal),
    freeUnits,
    pricePerUnit: qty > 0 ? Math.round(discountedTotal / qty) : unitPrice,
  };
}

export function getItemSubtotal(item: CartItem): number {
  if (item.product.promotion) {
    return calcPromo(item.product.promotion, item.quantity, item.product.price).discountedTotal;
  }
  return item.product.price * item.quantity;
}

export function getItemOriginalSubtotal(item: CartItem): number {
  return item.product.price * item.quantity;
}

// ─── Selectors ─────────────────────────────────────────────────────────────────

const selectCart = (state: { cart: CartState }) => state.cart;

export const selectItems      = (state: { cart: CartState }) => state.cart.items;
export const selectIsCartOpen = (state: { cart: CartState }) => state.cart.isCartOpen;

export const selectTotalItems = createSelector(
  selectCart,
  ({ items }) => items.reduce((sum, i) => sum + i.quantity, 0)
);

export const selectTotalPrice = createSelector(
  selectCart,
  ({ items }) => items.reduce((sum, i) => sum + getItemSubtotal(i), 0)
);

export const selectOriginalTotalPrice = createSelector(
  selectCart,
  ({ items }) => items.reduce((sum, i) => sum + getItemOriginalSubtotal(i), 0)
);

export const selectTotalSavings = createSelector(
  selectCart,
  ({ items }) => items.reduce((sum, i) => {
    if (i.product.promotion) {
      return sum + calcPromo(i.product.promotion, i.quantity, i.product.price).savings;
    }
    return sum;
  }, 0)
);
